import React, { useState, useEffect } from 'react';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import Toggle from '../../components/Toggle';
import appSettingsService, { AppSetting } from '../../services/appSettings.service';
import ChangeAllRepairPanel from './ChangeAllRepairPanel';
import PermissionSyncPanel from './PermissionSyncPanel';
import PrinterDefaultsSeedPanel from './PrinterDefaultsSeedPanel';
import { RouteEtaSettings } from './RouteEtaSettings';
import SamsaraRouteCleanupPanel from './SamsaraRouteCleanupPanel';

// Prod builds never show the permission sync card (the backend additionally
// refuses unless PERM_SYNC_ENABLED is set and the target isn't the source).
const isNonProdBuild = import.meta.env.VITE_ENV === 'test' || import.meta.env.DEV;

// Kill switch for the dispatch dashboard's live push (migration 391).
const DISPATCH_PUSH_KEY = 'dispatch_board.push_enabled';
// Kill switch for the At Risk Monitor's driver-ETA fetch (migration 392).
const AT_RISK_ETAS_KEY = 'at_risk.driver_etas_enabled';
// Kill switch for the Axle Mobility repair-order push (migration 398).
const AXLE_PUSH_KEY = 'axle.push_enabled';
const AXLE_INVOICE_PUSH_KEY = 'axle.invoice_push_enabled';
// Kill switch for repair orders driving the chassis In Service flag (migration 497).
const IN_SERVICE_TOGGLE_KEY = 'equipment.in_service_toggle_enabled';

const ApplicationSettings: React.FC = () => {
  const [settings, setSettings] = useState<AppSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await appSettingsService.getAll();
      setSettings(data);
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleMapsProviderToggle = async (checked: boolean) => {
    const newValue = checked ? 'google' : 'trimble';
    setSaving('maps_provider');
    try {
      await appSettingsService.update('maps_provider', newValue);
      setSettings(prev =>
        prev.map(s =>
          s.setting_key === 'maps_provider' ? { ...s, setting_value: newValue } : s
        )
      );
    } catch (err) {
      setError('Failed to update setting');
    } finally {
      setSaving(null);
    }
  };

  const handleDispatchPushToggle = async (checked: boolean) => {
    const newValue = checked ? 'true' : 'false';
    setSaving(DISPATCH_PUSH_KEY);
    try {
      await appSettingsService.update(DISPATCH_PUSH_KEY, newValue);
      setSettings(prev =>
        prev.map(s =>
          s.setting_key === DISPATCH_PUSH_KEY ? { ...s, setting_value: newValue } : s
        )
      );
    } catch (err) {
      setError('Failed to update setting');
    } finally {
      setSaving(null);
    }
  };

  const mapsProvider = settings.find(s => s.setting_key === 'maps_provider');
  const isGoogle = !mapsProvider || mapsProvider.setting_value === 'google';

  const handleBooleanToggle = async (key: string, checked: boolean) => {
    const newValue = checked ? 'true' : 'false';
    setSaving(key);
    try {
      await appSettingsService.update(key, newValue);
      setSettings(prev =>
        prev.map(s => (s.setting_key === key ? { ...s, setting_value: newValue } : s))
      );
    } catch (err) {
      setError('Failed to update setting');
    } finally {
      setSaving(null);
    }
  };

  const atRiskEtas = settings.find(s => s.setting_key === AT_RISK_ETAS_KEY);
  // Defaults ON when the row is absent, matching the backend, so a missing
  // migration doesn't make the UI claim ETAs are disabled.
  const isAtRiskEtasOn = (atRiskEtas?.setting_value ?? 'true').trim().toLowerCase() === 'true';

  const dispatchPush = settings.find(s => s.setting_key === DISPATCH_PUSH_KEY);
  // Off unless the row explicitly says on — matches the backend, which reads the
  // same row uncached and fails closed.
  const isDispatchPushOn = (dispatchPush?.setting_value || '').trim().toLowerCase() === 'true';

  const axlePush = settings.find(s => s.setting_key === AXLE_PUSH_KEY);
  // Off unless the row explicitly says on — same fail-closed contract as the
  // backend's uncached read (services/axle/push.py).
  const isAxlePushOn = (axlePush?.setting_value || '').trim().toLowerCase() === 'true';

  const axleInvoicePush = settings.find(s => s.setting_key === AXLE_INVOICE_PUSH_KEY);
  const isAxleInvoicePushOn = (axleInvoicePush?.setting_value || '').trim().toLowerCase() === 'true';

  const inServiceToggle = settings.find(s => s.setting_key === IN_SERVICE_TOGGLE_KEY);
  // Off unless the row explicitly says on — the backend reads the same row
  // uncached and fails closed (services/equipment_in_service.py).
  const isInServiceToggleOn = (inServiceToggle?.setting_value || '').trim().toLowerCase() === 'true';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <Cog6ToothIcon className="h-6 w-6 text-gray-500" />
        <h1 className="text-xl font-semibold text-heading">Application Settings</h1>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Maps Provider Section */}
      <div className="bg-panel shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-heading mb-4">Routing</h2>

        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-label">Map Provider:</label>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${!isGoogle ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>
              Trimble
            </span>
            <Toggle
              checked={isGoogle}
              onChange={handleMapsProviderToggle}
              disabled={saving === 'maps_provider'}
              name="maps_provider"
              ariaLabel="Toggle maps provider between Trimble and Google"
            />
            <span className={`text-sm font-medium ${isGoogle ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>
              Google
            </span>
          </div>
          {saving === 'maps_provider' && (
            <span className="text-xs text-gray-400">Saving...</span>
          )}
        </div>
        <p className="mt-2 text-xs text-muted">
          Controls which map provider is used for distance and duration calculations across the application.
        </p>
        {mapsProvider?.updated_by && (
          <p className="mt-1 text-xs text-gray-400">
            Last updated by {mapsProvider.updated_by} on {mapsProvider.updated_at ? new Date(mapsProvider.updated_at).toLocaleString() : 'N/A'}
          </p>
        )}
      </div>

      {/* Realtime Section */}
      <div className="bg-panel shadow rounded-lg p-6 mt-6">
        <h2 className="text-lg font-medium text-heading mb-4">Realtime</h2>

        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-label">
            Dispatch Board Live Updates:
          </label>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${!isDispatchPushOn ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>
              Off
            </span>
            <Toggle
              checked={isDispatchPushOn}
              onChange={handleDispatchPushToggle}
              disabled={saving === DISPATCH_PUSH_KEY}
              name={DISPATCH_PUSH_KEY}
              ariaLabel="Toggle dispatch board live updates"
            />
            <span className={`text-sm font-medium ${isDispatchPushOn ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>
              On
            </span>
          </div>
          {saving === DISPATCH_PUSH_KEY && (
            <span className="text-xs text-gray-400">Saving...</span>
          )}
        </div>
        <p className="mt-2 text-xs text-muted">
          Pushes dispatch dashboard changes to open boards as they happen, instead of waiting for
          the 30-second refresh. Turning this off stops the push immediately on every server — the
          dashboard keeps refreshing on its 30-second timer, which is how it worked before this
          setting existed. Safe to turn off at any time.
        </p>
        {dispatchPush?.updated_by && (
          <p className="mt-1 text-xs text-gray-400">
            Last updated by {dispatchPush.updated_by} on {dispatchPush.updated_at ? new Date(dispatchPush.updated_at).toLocaleString() : 'N/A'}
          </p>
        )}

        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-label">
              At Risk Monitor Driver ETAs:
            </label>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${!isAtRiskEtasOn ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>
                Off
              </span>
              <Toggle
                checked={isAtRiskEtasOn}
                onChange={(checked) => handleBooleanToggle(AT_RISK_ETAS_KEY, checked)}
                disabled={saving === AT_RISK_ETAS_KEY}
                name={AT_RISK_ETAS_KEY}
                ariaLabel="Toggle At Risk Monitor driver ETAs"
              />
              <span className={`text-sm font-medium ${isAtRiskEtasOn ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>
                On
              </span>
            </div>
            {saving === AT_RISK_ETAS_KEY && (
              <span className="text-xs text-gray-400">Saving...</span>
            )}
          </div>
          <p className="mt-2 text-xs text-muted">
            The At Risk Monitor fetches projected driver ETAs after loading its rows. That call has
            been measured at 1&ndash;6 seconds and the page waits on it before showing the table.
            Turn this off to load the page without ETAs &mdash; every other column is unaffected.
            The Driver Status Monitor is not affected by this setting.
          </p>
          {atRiskEtas?.updated_by && (
            <p className="mt-1 text-xs text-gray-400">
              Last updated by {atRiskEtas.updated_by} on {atRiskEtas.updated_at ? new Date(atRiskEtas.updated_at).toLocaleString() : 'N/A'}
            </p>
          )}
        </div>
      </div>

      {/* Integrations Section */}
      <div className="bg-panel shadow rounded-lg p-6 mt-6">
        <h2 className="text-lg font-medium text-heading mb-4">Integrations</h2>

        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-label">
            Axle Repair Order Push:
          </label>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${!isAxlePushOn ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>
              Off
            </span>
            <Toggle
              checked={isAxlePushOn}
              onChange={(checked) => handleBooleanToggle(AXLE_PUSH_KEY, checked)}
              disabled={saving === AXLE_PUSH_KEY}
              name={AXLE_PUSH_KEY}
              ariaLabel="Toggle the Axle repair order push"
            />
            <span className={`text-sm font-medium ${isAxlePushOn ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>
              On
            </span>
          </div>
          {saving === AXLE_PUSH_KEY && (
            <span className="text-xs text-gray-400">Saving...</span>
          )}
        </div>
        <p className="mt-2 text-xs text-muted">
          Sends each repair order to Axle Mobility when it is completed, and again whenever a
          pushed order changes afterward. Turning this off stops all sends immediately on every
          server &mdash; including the retry sweep &mdash; and repair-order saves are never affected
          either way. Failed sends are retried automatically every 5 minutes while this is on.
        </p>
        {axlePush?.updated_by && (
          <p className="mt-1 text-xs text-gray-400">
            Last updated by {axlePush.updated_by} on {axlePush.updated_at ? new Date(axlePush.updated_at).toLocaleString() : 'N/A'}
          </p>
        )}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-label">Axle Ramp Invoice Delivery:</label>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${!isAxleInvoicePushOn ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>Off</span>
              <Toggle
                checked={isAxleInvoicePushOn}
                onChange={(checked) => handleBooleanToggle(AXLE_INVOICE_PUSH_KEY, checked)}
                disabled={saving === AXLE_INVOICE_PUSH_KEY || !axleInvoicePush}
                name={AXLE_INVOICE_PUSH_KEY}
                ariaLabel="Toggle Axle Ramp invoice delivery"
              />
              <span className={`text-sm font-medium ${isAxleInvoicePushOn ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>On</span>
            </div>
            {saving === AXLE_INVOICE_PUSH_KEY && <span className="text-xs text-gray-400">Saving...</span>}
          </div>
          <p className="mt-2 text-xs text-muted">
            Enables queued Maintenance invoice PDFs to be delivered to Axle’s live production Inbox,
            including automatic delivery by the enabled Axle scheduler job. Enable in only one environment.
            The backend must also have AXLE_INVOICE_ENABLED=true. Scanning Ramp remains available while this is off.
          </p>
          {!axleInvoicePush && <p className="mt-2 text-xs text-muted">Invoice delivery setting is unavailable. Apply migration 500 before enabling.</p>}
          {axleInvoicePush?.updated_by && <p className="mt-1 text-xs text-gray-400">
            Last updated by {axleInvoicePush.updated_by} on {axleInvoicePush.updated_at ? new Date(axleInvoicePush.updated_at).toLocaleString() : 'N/A'}
          </p>}
        </div>
      </div>

      {/* Fleet Section */}
      <div className="bg-panel shadow rounded-lg p-6 mt-6">
        <h2 className="text-lg font-medium text-heading mb-4">Fleet</h2>

        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-label">
            Repair Orders Set Chassis In Service:
          </label>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${!isInServiceToggleOn ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>
              Off
            </span>
            <Toggle
              checked={isInServiceToggleOn}
              onChange={(checked) => handleBooleanToggle(IN_SERVICE_TOGGLE_KEY, checked)}
              disabled={saving === IN_SERVICE_TOGGLE_KEY}
              name={IN_SERVICE_TOGGLE_KEY}
              ariaLabel="Toggle repair orders driving the chassis In Service flag"
            />
            <span className={`text-sm font-medium ${isInServiceToggleOn ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`}>
              On
            </span>
          </div>
          {saving === IN_SERVICE_TOGGLE_KEY && (
            <span className="text-xs text-gray-400">Saving...</span>
          )}
        </div>
        <p className="mt-2 text-xs text-muted">
          A chassis with an open, dispatched, pending, or deferred repair order has its In Service
          status set to N, and back to Y once every repair order on it is completed or closed. Only
          Y and N ever change &mdash; STACKED, STORAGE, and ROADONE are left alone. Applies on each
          repair-order save and on a 15-minute sweep that also catches changes made in V1. Turning
          this off stops both immediately on every server; the status is simply never touched.
        </p>
        {inServiceToggle?.updated_by && (
          <p className="mt-1 text-xs text-gray-400">
            Last updated by {inServiceToggle.updated_by} on {inServiceToggle.updated_at ? new Date(inServiceToggle.updated_at).toLocaleString() : 'N/A'}
          </p>
        )}
      </div>

      {/* Data Repair Section */}
      {settings.some(s => s.setting_key === 'samsara_route_eta.enabled') && (
        <RouteEtaSettings settings={settings} onSaved={loadSettings} />
      )}

      <SamsaraRouteCleanupPanel />

      <ChangeAllRepairPanel />

      <PrinterDefaultsSeedPanel />

      {/* Permission Sync Section (non-prod builds only) */}
      {isNonProdBuild && <PermissionSyncPanel />}
    </div>
  );
};

export default ApplicationSettings;
